/**
 * The little scene above a palette hint — the "what are you looking for" prompt
 * and the "nothing matched" result.
 *
 * A drawn illustration rather than a Lucide glyph in a grey rounded square. At
 * the size these states occupy, a 20px icon in a tile read as a disabled button
 * rather than an illustration, and the two states looked identical apart from a
 * cross that was too small to notice.
 *
 * Everything is stroked in `currentColor` and filled from theme tokens, so it
 * inherits light/dark from the text colour around it and needs no dark-mode
 * variant. `strokeLinecap="round"` throughout is what keeps it friendly rather
 * than technical.
 */
export function SearchStateArt({ variant }: { variant: 'prompt' | 'empty' }) {
  const isEmpty = variant === 'empty';

  return (
    <svg
      viewBox="0 0 64 64"
      className="h-16 w-16 text-muted-foreground"
      fill="none"
      // Decorative: the title underneath already names the state, so announcing
      // this would just repeat it.
      aria-hidden="true"
      focusable="false"
    >
      {/* Soft disc, so the art sits on something instead of floating in the
          middle of a white sheet. */}
      <circle cx="32" cy="32" r="26" className="fill-muted" />

      {/* Lens. Slightly off-centre with the handle running to the lower right —
          a perfectly centred magnifier reads as an icon, not a picture. */}
      <circle cx="28" cy="28" r="13" className="stroke-current" strokeWidth="2.5" opacity="0.75" />
      <path
        d="M38 38 L47 47"
        className="stroke-current"
        strokeWidth="2.5"
        strokeLinecap="round"
        opacity="0.75"
      />

      {isEmpty ? (
        /* Nothing found: a single stroke through the lens. A full × read as an
           error; one line reads as "empty". */
        <path
          d="M23 28 H33"
          className="stroke-current"
          strokeWidth="2.5"
          strokeLinecap="round"
          opacity="0.55"
        />
      ) : (
        /* Waiting for a query: two suggestion lines inside the lens, like rows
           of results about to arrive. */
        <>
          <path
            d="M22 25 H34"
            className="stroke-current"
            strokeWidth="2.5"
            strokeLinecap="round"
            opacity="0.45"
          />
          <path
            d="M22 31 H30"
            className="stroke-current"
            strokeWidth="2.5"
            strokeLinecap="round"
            opacity="0.3"
          />
        </>
      )}

      {/* Two specks of accent, off the disc, so the whole thing has some life
          without competing with the message under it. */}
      <circle cx="51" cy="19" r="2.5" className="fill-primary" opacity="0.55" />
      <circle cx="14" cy="46" r="1.75" className="fill-primary" opacity="0.35" />
    </svg>
  );
}
